import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Edit2, Trash2, Users, ShieldCheck, Save, Loader2, Eye, Pencil, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULES = [
  { key: 'tasks', label: 'Tasks' },
  { key: 'production', label: 'Production' },
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'diesel', label: 'Diesel' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'parts_release', label: 'Parts Release' },
  { key: 'purchase_orders', label: 'Purchase Orders' },
  { key: 'assets', label: 'Assets' },
  { key: 'tyres', label: 'Tyres' },
  { key: 'scrap', label: 'Scrap' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'activity_log', label: 'Activity Log' },
  { key: 'parts_directory', label: 'Parts Directory' },
];

const COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#0ea5e9', '#14b8a6', '#f97316'];

const emptyPermissions = () =>
  Object.fromEntries(MODULES.map(m => [m.key, { view: false, edit: false, approve_deletions: false }]));

const emptyRole = () => ({
  name: '', description: '', color: COLORS[0], department: '',
  permissions: emptyPermissions(), can_manage_users: false,
});

const RoleManagementPage = ({ user }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null); // null = create mode
  const [form, setForm] = useState(emptyRole());
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/roles`, { headers: { Authorization: `Bearer ${token}` } });
      setRoles(res.data);
    } catch (e) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoles(); }, []);

  const openCreate = () => {
    setEditingRole(null);
    setForm(emptyRole());
    setIsDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    const perms = emptyPermissions();
    for (const [mod, p] of Object.entries(role.permissions || {})) {
      if (perms[mod]) perms[mod] = { ...perms[mod], ...p };
    }
    setForm({
      name: role.name,
      description: role.description || '',
      color: role.color || COLORS[0],
      department: role.department || '',
      permissions: perms,
      can_manage_users: role.can_manage_users || false,
    });
    setIsDialogOpen(true);
  };

  const setModulePerm = (module, action, value) => {
    setForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: { ...prev.permissions[module], [action]: value }
      }
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Role name is required'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Role updated');
      } else {
        await axios.post(`${API}/roles`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Role created');
      }
      setIsDialogOpen(false);
      fetchRoles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (roleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/roles/${roleId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Role deleted');
      setDeleteConfirmId(null);
      fetchRoles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete role');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-indigo-600" />
            Role Management
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Define custom roles with per-module permissions. Assign roles to users in User Management.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-role-btn">
          <Plus className="h-4 w-4 mr-2" /> New Role
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : roles.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <ShieldCheck className="h-12 w-12 mx-auto mb-3 text-slate-200" />
          <p className="font-semibold text-slate-600">No custom roles yet</p>
          <p className="text-sm text-slate-400 mt-1">Create roles to assign to department heads and team members</p>
          <Button onClick={openCreate} className="mt-4 bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4 mr-2" /> Create First Role
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {roles.map(role => (
            <div key={role.id} className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid={`role-card-${role.id}`}>
              <div className="h-1.5" style={{ backgroundColor: role.color }} />
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-slate-900 text-lg">{role.name}</h3>
                    {role.department && (
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                        {role.department}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(role)} data-testid={`edit-role-${role.id}`}>
                      <Edit2 className="h-3.5 w-3.5 text-slate-500" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setDeleteConfirmId(role.id)} data-testid={`delete-role-${role.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>
                {role.description && <p className="text-sm text-slate-500 mb-3">{role.description}</p>}
                <div className="flex flex-wrap gap-1 mb-3">
                  {role.can_manage_users && (
                    <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                      <Users className="h-3 w-3" /> Can Manage Team
                    </span>
                  )}
                  {Object.entries(role.permissions || {}).filter(([, p]) => p.approve_deletions).map(([m]) => (
                    <span key={m} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                      Approves {m}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                  <Users className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-xs text-slate-500">{role.user_count || 0} user{role.user_count !== 1 ? 's' : ''} assigned</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Role Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
              {editingRole ? 'Edit Role' : 'Create New Role'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role Name <span className="text-red-500">*</span></Label>
                <Input
                  className="mt-1.5"
                  placeholder="e.g. Head of Production"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  data-testid="role-name-input"
                />
              </div>
              <div>
                <Label>Department</Label>
                <Input
                  className="mt-1.5"
                  placeholder="e.g. Production, Diesel, Maintenance"
                  value={form.department}
                  onChange={e => setForm(p => ({ ...p, department: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                className="mt-1.5 resize-none"
                rows={2}
                placeholder="Brief description of this role's responsibilities"
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div>
              <Label>Role Colour</Label>
              <div className="flex gap-2 mt-1.5">
                {COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, color: c }))}
                    className={`h-7 w-7 rounded-full border-2 transition-transform ${form.color === c ? 'border-slate-900 scale-125' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>

            {/* Permission Matrix */}
            <div>
              <Label className="text-base font-semibold">Module Permissions</Label>
              <p className="text-xs text-slate-500 mb-3">
                Configure what this role can view, edit, and approve deletions for.
              </p>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-40">Module</TableHead>
                      <TableHead className="text-center w-24">
                        <div className="flex items-center justify-center gap-1">
                          <Eye className="h-3.5 w-3.5" /> View
                        </div>
                      </TableHead>
                      <TableHead className="text-center w-24">
                        <div className="flex items-center justify-center gap-1">
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </div>
                      </TableHead>
                      <TableHead className="text-center w-36">
                        <div className="flex items-center justify-center gap-1 text-red-600">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Approve Del.
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {MODULES.map(mod => (
                      <TableRow key={mod.key}>
                        <TableCell className="font-medium text-sm">{mod.label}</TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={form.permissions[mod.key]?.view ?? false}
                            onCheckedChange={v => setModulePerm(mod.key, 'view', v)}
                            data-testid={`perm-${mod.key}-view`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={form.permissions[mod.key]?.edit ?? false}
                            onCheckedChange={v => setModulePerm(mod.key, 'edit', v)}
                            data-testid={`perm-${mod.key}-edit`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={form.permissions[mod.key]?.approve_deletions ?? false}
                            onCheckedChange={v => setModulePerm(mod.key, 'approve_deletions', v)}
                            data-testid={`perm-${mod.key}-approve`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* HR / Team Management */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-emerald-900 font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4" /> Can Manage Team (HR)
                  </Label>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    Users with this role can add members to their department in User Management
                  </p>
                </div>
                <Switch
                  checked={form.can_manage_users}
                  onCheckedChange={v => setForm(p => ({ ...p, can_manage_users: v }))}
                  data-testid="role-can-manage-users"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving || !form.name.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-role-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingRole ? 'Save Changes' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Delete Role?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This action cannot be undone. Roles with assigned users cannot be deleted.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteConfirmId)}>
              Delete Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleManagementPage;
